'use client';

import {Box, Stack, Text, Button, Image, Portal, Select, createListCollection, HStack, Icon} from "@chakra-ui/react";
import {useState, useMemo, useEffect} from "react";
import {closeDrawer, openDrawer} from "@/shared/store/drawer";
import {getImageSrc} from "@/utils/file";
import {beautifyPrice} from "@/utils/price";
import {MdChevronRight} from "react-icons/md";
import {GiftBox} from "@/types/giftbox";
import {CloseButton} from "../ui/close-button";
import ChevronDownSvgIcon from "@/shared/icons/ChevronDownSvgIcon";
import {sortSizes} from "@/utils/sizes";
import { useTranslations } from "next-intl";

export function GiftBoxBlockSelector({
  block,
  onSelectionChange,
}: {
  block: GiftBox['blocks'][0];
  onSelectionChange?: (selection: {
    blockId: string;
    selectedIndex: number;
    selectedSize: string | null;
    isValid: boolean;
  }) => void;
}) {
  const t = useTranslations();
  const items = block.items.filter((i: any) => i.product);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const selectedItem = items[selectedIndex];

  const sizes = (selectedItem.product.stocks?.map((stock: any) => stock.sizeName) || [])
    .filter((size: string | null) => size !== null && block.sizes.includes(size));
  const [selectedSize, setSelectedSize] = useState("");

  const canChooseSize = sizes.length > 0;

  const otherItems = items.map((item: any, idx: number) => ({ item, idx }))
    .filter((_, idx) => idx !== selectedIndex);

  const stocks = selectedItem.product?.stocks || [];

  const isValid = (() => {
    // Если у товара есть размеры (sizeName не null)
    const hasSizes = stocks.some((s: any) => s.sizeName !== null);

    if (hasSizes) {
      if (!selectedSize) {
        console.debug("isValid: Размер не выбран");
        return false;
      }
      const sizeInStock = stocks.some((s: any) => s.sizeName === selectedSize && s.amount > 0);
      if (!sizeInStock) {
        console.debug("isValid: Выбранный размер отсутствует на складе");
        return false;
      }
      return true;
    } else {
      const universalInStock = stocks.some((s: any) => s.sizeName === null && s.amount > 0);
      if (!universalInStock) {
        console.debug("isValid: Универсальный товар отсутствует на складе");
        return false;
      }
      return true;
    }
  })();
  console.debug(sizes)
  // Сброс выбранного размера, если он невалиден для нового товара
  useEffect(() => {
    if (selectedSize && !sizes.includes(selectedSize)) {
      setSelectedSize("");
    }
  }, [selectedIndex, sizes]);

  // Вызов onSelectionChange только для передачи наверх
  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange({
        blockId: block.id,
        selectedIndex,
        selectedSize: canChooseSize ? selectedSize : null,
        isValid,
      });
    }
  }, [selectedIndex, selectedSize, canChooseSize, isValid]);

  function handleChooseOtherClick() {
    openDrawer({
      type: "custom",
      title: (
        <Text textStyle="bold16" textTransform={"unset"}>{t('select_another_product')}</Text>
      ),
      content: (
        <Stack gap={4}>
          {otherItems.map(({item, idx}) => (
            <HStack
              key={item.id}
              p={3}
              alignItems="stretch"
              gap={3}
            >
              <Image
                src={getImageSrc(item.product)}
                alt={item.product.name}
                width={120}
                height={150}
                style={{objectFit: "cover", borderRadius: "6px"}}
              />
              <Stack justify={"space-between"} flex={1}>
                <Stack gap="4px">
                  <Text textStyle="bold18">{item.product.name}</Text>
                  <Text textStyle="bold16">{item.product.price}₸</Text>
                </Stack>
                <Button w="100%" textStyle={"bold14"} variant="qr_secondary" borderRadius={"none"} onClick={(e) => {
                  setSelectedIndex(idx);
                  closeDrawer();
                }}>
                  {t('choose')}
                </Button>
              </Stack>
            </HStack>
          ))}
        </Stack>
      ),
    });
  }

  function resetSelection() {
    setSelectedIndex(0);
    setSelectedSize("");
  }

  const sizeCollection = useMemo(
    () =>
      createListCollection({
        items: sortSizes(sizes)?.map((size: string) => ({
          label: size,
          value: size,
          disabled: stocks.some((s: any) => s.sizeName === size && s.amount <= 0)
        })),
      }),
    [sizes]
  );

  // useEffect для сброса размера только если он невалиден
  useEffect(() => {
    if (selectedSize && !sizes.includes(selectedSize)) {
      setSelectedSize("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndex, sizes]);

  return (
    <Box
      display="flex"
      gap={4}
      flexDirection={{base: "column", md: "row"}}
      maxW={{base: "160px", md: "none"}}
      position={"relative"}
    >
      <Box minW={{base: "154px", md: "74px"}} maxW="92px">
        <Image
          src={getImageSrc(selectedItem.product)}
          alt={selectedItem.product.name}
          width={{base: "154px", md: "74px"}}
          height="92px"
          borderRadius="md"
          objectFit={{base: "contain", md: "cover"}}
        />
      </Box>
      <Stack flex="1" direction={{base: "column", lg: "row"}} justify={{sm: "initial", md: "space-between"}} gap={4}>
        <Box>
          <Text textStyle="bold14" minHeight={{base: "2.5rem"}}
                lineClamp={{base: 2, md: 0}}>{selectedItem.product.name}</Text>
          <HStack gap={1}>
            <Text textStyle="bold12">{beautifyPrice(selectedItem.product.price)}</Text>
            <Text textStyle={"bold12"} color={"text.secondary"}>x1</Text>
          </HStack>
        </Box>
        <Stack direction={{base: "column", sm: "row"}} justify="space-between" align={"baseline"}>
          <Stack w={"100%"} alignItems={{base: 'stretch', md: 'start'}} direction={{base: "column", md: "column-reverse"}} gap={{base: "17px", md: "8px"}}>
            <Button onClick={handleChooseOtherClick} variant="plain" size={null as any} px={0}
                    justifyContent={{base: "flex-start", md: "flex-end"}} textStyle={"bold14"} color={"brand"} gap={0}>
              {t('select_another')} <Icon as={MdChevronRight} boxSize={5}/>
            </Button>
            <HStack gap="12px" justify="flex-end">
              {!canChooseSize && (
                <Box width={{base: '100%', sm: "180px"}}></Box>
              )}
              {canChooseSize && (
                <Select.Root
                  collection={sizeCollection}
                  width="100%"
                  minW={{base: '100%', md: '180px'}}
                  maxW={'180px'}
                  textStyle={"bold14"}
                  value={selectedSize ? [selectedSize] : []}
                  onValueChange={(e) => setSelectedSize(e.value[0])}
                >
                  <Select.HiddenSelect/>
                  <Select.Control>
                    <Select.Trigger
                      css={{
                        'width': '100%',
                        "&[data-placeholder-shown] > *": {
                          color: "text.primary",
                          textTransform: "lowercase",
                        },
                      }}
                      borderRadius={"none"} borderColor={"stroke"} justifyContent={{base: "center", md: "flex-start"}}>
                      <Select.ValueText textStyle={"bold14"} textTransform={"uppercase"} maxWidth={"100%"}
                                        placeholder={t('select_size')}/>
                    </Select.Trigger>
                    <Select.IndicatorGroup display={{base: "none", md: "flex"}} alignItems="center">
                      <Icon as={ChevronDownSvgIcon} boxSize={5} color="text.primary"/>
                    </Select.IndicatorGroup>
                  </Select.Control>
                  <Portal>
                    <Select.Positioner>
                      <Select.Content borderRadius={"none"} boxShadow={"0 14px 20px 0 rgba(0, 0, 0, 0.5)"}>
                        {sizeCollection.items.map((item: any) => (
                          <Select.Item textStyle={'medium16'} key={item.value} item={item}>
                            {item.label}
                            <Select.ItemIndicator/>
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Positioner>
                  </Portal>
                </Select.Root>
              )}
              <CloseButton
                iconProps={{
                  boxSize: {base: "14px", md: "16px"},
                }}
                color={'text.primary'}
                variant="qr_secondary"
                onClick={resetSelection}
                aria-label="Сбросить выбор"
                position={{base: "absolute", md: "static"}}
                top={{base: "0", md: "auto"}}
                right={{base: "0", md: "auto"}}
                zIndex={2}
                minHeight={'40px'}
                maxHeight={'40px'}
                ml={{md: 0}}
                minH="40px"
              />
            </HStack>
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
}
